import { lazy, Suspense, useEffect, useState } from 'react'
import { cn } from '@shared/lib/cn'
import { usePrefersReducedMotion } from '@shared/lib/usePrefersReducedMotion'

const LottiePlayer = lazy(() => import('./LottiePlayer'))

export interface IllustrationProps {
  /** Статичная картинка. Рисуется сразу и остаётся, пока не приедет анимация. */
  src?: string
  alt?: string
  /** `() => import('...json')` — только так, чтобы Vite вынес каждую анимацию в
   *  свой чанк и она не попала в чанк страницы. */
  loadAnimation?: () => Promise<{ default: object }>
  /** Размер задаёт вызывающий: в карточке входа он один, в модалке — другой. */
  className?: string
}

/**
 * Иллюстрация состояния: статичная картинка плюс подгружаемая поверх неё
 * Lottie-анимация.
 *
 * Вынесена из `AuthCard`, где этот приём появился первым: модалкам результата
 * нужен ровно тот же — картинка видна мгновенно, анимация приезжает отдельным
 * чанком, а при `prefers-reduced-motion` не приезжает вовсе.
 */
export function Illustration({ src, alt, loadAnimation, className }: IllustrationProps) {
  const [animationData, setAnimationData] = useState<object | null>(null)
  const reducedMotion = usePrefersReducedMotion()

  useEffect(() => {
    if (!loadAnimation || reducedMotion) return
    let cancelled = false
    void loadAnimation().then((mod) => {
      if (!cancelled) setAnimationData(mod.default)
    })
    return () => {
      cancelled = true
    }
  }, [loadAnimation, reducedMotion])

  const placeholder = src ? <img src={src} alt={alt ?? ''} className={cn(className)} /> : null

  if (!animationData) return placeholder

  return (
    <Suspense fallback={placeholder}>
      <div className={cn(className)}>
        <LottiePlayer data={animationData} />
      </div>
    </Suspense>
  )
}
